import React, { useEffect, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { ROLES, getStep2Config, completeOnboarding, getOnboardingProfile } from './OnboardingWizard.jsx';
import { EXAM_TYPE_LABELS, resolveClampedExamType } from './clinical/clinicalOptions.js';
import { deriveTierFromRoleAndExam, saveOnboardingProfile } from '../firebase/userProfile.js';
import { getAuthInstance, trackEvent, setAnalyticsUserProps } from '../firebase/config.js';

const TIER_LABELS = {
    board_review: 'Board Review',
    specialty_boards: 'Specialty Boards',
};

const selectClass =
    'w-full rounded-xl bg-slate-800/70 border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500/40';

const Field = ({ label, children }) => (
    <div>
        <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
        {children}
    </div>
);

const SectionHeading = ({ children }) => (
    <h3 className="text-sm font-semibold text-white mb-3">{children}</h3>
);

const SettingsModal = ({ onClose, clinicalSettings, onClinicalSettingsChange, entitlements, onOpenAccount, onOpenPlanChooser }) => {
    const initialProfile = getOnboardingProfile() || {};
    const [role, setRole] = useState(initialProfile.role || '');
    const [examTarget, setExamTarget] = useState(initialProfile.examTarget || '');
    const [subjects, setSubjects] = useState(initialProfile.subjects || []);
    const [activeSubject, setActiveSubject] = useState(initialProfile.examTarget || '');
    const [savedPulse, setSavedPulse] = useState(false);

    const step2Config = role ? getStep2Config(role) : null;
    const isMultiSubject = !!step2Config?.multi;
    const currentTier = entitlements?.tier || 'free';

    const handleRoleChange = (id) => {
        setRole(id);
        setExamTarget('');
        setSubjects([]);
        setActiveSubject('');
    };

    const toggleSubject = (id) => {
        setSubjects(prev => {
            const next = prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id];
            if (!next.includes(activeSubject)) setActiveSubject(next[0] || '');
            return next;
        });
    };

    // Persist role/exam-target/subject changes as soon as a valid selection exists,
    // resolve the actual generation examType, and clamp it if it's above the user's
    // current entitlement tier (never blocks the selection itself — just what
    // actually gets sent to the generation engine until they upgrade).
    useEffect(() => {
        const validSelection = isMultiSubject ? subjects.length > 0 : !!examTarget;
        if (!role || !validSelection) return;

        const nextProfile = {
            role,
            examTarget: isMultiSubject ? (activeSubject || subjects[0]) : examTarget,
            subjects: isMultiSubject ? subjects : [],
            tier: deriveTierFromRoleAndExam(role, isMultiSubject ? (activeSubject || subjects[0]) : examTarget),
            focus: isMultiSubject ? 'class_prep' : examTarget,
        };
        completeOnboarding(nextProfile);
        (async () => {
            try {
                const auth = getAuthInstance();
                const u = auth?.currentUser;
                if (u && !u.isAnonymous) await saveOnboardingProfile(u.uid, nextProfile);
            } catch (_) {}
        })();

        const { resolved: resolvedExamType, examType: finalExamType } = resolveClampedExamType({
            examTarget: nextProfile.examTarget,
            subjects: nextProfile.subjects,
            isMultiSubject,
            activeSubject,
            entitlementsTier: currentTier,
        });

        if (finalExamType && finalExamType !== clinicalSettings.examType) {
            onClinicalSettingsChange({ ...clinicalSettings, examType: finalExamType });
        }

        try {
            setAnalyticsUserProps({ prep_tier: nextProfile.tier, exam_type: resolvedExamType });
            trackEvent('prep_level_set', { role, examType: resolvedExamType, tier: nextProfile.tier, entry_point: 'settings' });
        } catch (_) {}

        setSavedPulse(true);
        const t = setTimeout(() => setSavedPulse(false), 1200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role, examTarget, subjects, activeSubject]);

    const { resolved: lockedExamType, requiredTier: lockedRequiredTier, isLocked: isCurrentSelectionLocked } = resolveClampedExamType({
        examTarget: isMultiSubject ? (activeSubject || subjects[0]) : examTarget,
        subjects,
        isMultiSubject,
        activeSubject,
        entitlementsTier: currentTier,
    });

    const handleUpgradeClick = () => {
        try {
            trackEvent('prep_level_upgrade_prompted', { attemptedExamType: lockedExamType, requiredTier: lockedRequiredTier, currentTier });
        } catch (_) {}
        onClose?.();
        onOpenPlanChooser?.({
            isClinicals: true,
            reason: `${EXAM_TYPE_LABELS[lockedExamType] || lockedExamType} requires the ${TIER_LABELS[lockedRequiredTier] || lockedRequiredTier} plan.`,
        });
    };

    return (
        <Modal title="Settings" onClose={onClose} size="xl">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <SectionHeading>Training profile</SectionHeading>
                    {savedPulse && <span className="text-[11px] text-emerald-400">Saved ✓</span>}
                </div>
                <div className="space-y-4 -mt-3">
                    <Field label="Stage of training">
                        <select className={selectClass} value={role} onChange={(e) => handleRoleChange(e.target.value)}>
                            <option value="" disabled>Select your stage...</option>
                            {ROLES.map((r) => (
                                <option key={r.id} value={r.id}>{r.label}</option>
                            ))}
                        </select>
                    </Field>

                    {step2Config && (
                        <Field label={step2Config.label}>
                            {step2Config.multi ? (
                                <div className="grid grid-cols-2 gap-2">
                                    {step2Config.options.map(({ id, label }) => {
                                        const selected = subjects.includes(id);
                                        return (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => toggleSubject(id)}
                                                className={`rounded-lg px-3 py-2 text-left text-xs font-medium border transition ${
                                                    selected
                                                        ? 'border-teal-400/50 bg-teal-500/10 text-teal-200'
                                                        : 'border-white/10 bg-slate-900/40 text-slate-300 hover:border-white/20'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <select className={selectClass} value={examTarget} onChange={(e) => setExamTarget(e.target.value)}>
                                    <option value="" disabled>Select...</option>
                                    {step2Config.options.map(({ id, label }) => (
                                        <option key={id} value={id}>{label}</option>
                                    ))}
                                </select>
                            )}
                        </Field>
                    )}

                    {isMultiSubject && subjects.length > 1 && (
                        <Field label="Generating for">
                            <select className={selectClass} value={activeSubject || subjects[0]} onChange={(e) => setActiveSubject(e.target.value)}>
                                {subjects.map((id) => (
                                    <option key={id} value={id}>{EXAM_TYPE_LABELS[id] || id}</option>
                                ))}
                            </select>
                        </Field>
                    )}
                </div>

                {isCurrentSelectionLocked && (
                    <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex items-center justify-between gap-3 flex-wrap">
                        <span>
                            Your goal ({EXAM_TYPE_LABELS[lockedExamType] || lockedExamType}) needs the {TIER_LABELS[lockedRequiredTier] || lockedRequiredTier} plan — generating at your current plan's level until you upgrade.
                        </span>
                        <button
                            type="button"
                            onClick={handleUpgradeClick}
                            className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400 transition"
                        >
                            Upgrade
                        </button>
                    </div>
                )}

                <div className="border-t border-white/8 pt-5">
                    <button
                        type="button"
                        onClick={() => { onClose?.(); onOpenAccount?.(); }}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition text-slate-200"
                    >
                        Account Settings
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SettingsModal;
